// Transitions PR-1 — loan exit-path workflow records + itemized payoff
// quotes. Reads ride Loans.Read; writes gate on Transitions.Manage
// server-side. The Prepayment Notice is irrevocable once recorded (§2.5) —
// the server refuses cancel-from-Scheduled without the explicit override.

import { api } from "@/api/client";

export type LoanTransitionType =
  | "PhoenixBackedResale"
  | "SaleWithAssumption"
  | "BankRefinance"
  | "CashPayoff"
  | "HardshipExit"
  | "BmspFullPayoff"
  | "BmspThirdPartyRefinance"
  | "BmspMaturityExtension"
  | "RightSizeRefinance";

export type LoanTransitionStatus =
  | "Draft"
  | "Initiated"
  | "AwaitingLinkedDeal"
  | "PendingApproval"
  | "Scheduled"
  | "Completed"
  | "Canceled"
  | "Lapsed";

export interface LoanTransitionDto {
  id: string;
  loanId: string;
  type: LoanTransitionType;
  status: LoanTransitionStatus;
  linkedDealId: string | null;
  linkedNewLoanId: string | null;
  noticeDeliveredAt: string | null;
  tenderDate: string | null;
  noticeQuoteId: string | null;
  daysToTender: number | null;
  scheduledClosingDate: string | null;
  initiatedAt: string | null;
  completedAt: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  initiatedByUserId: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  executedPaymentId: string | null;
  openedDefaultCaseId: string | null;
  parametersJson: string | null;
  createdAt: string;
  legalNextStatuses: LoanTransitionStatus[];
}

export interface LoanTransitionEventDto {
  id: string;
  kind: string;
  at: string;
  byUserId: string | null;
  note: string | null;
  documentId: string | null;
}

export interface LoanTransitionDetailDto {
  transition: LoanTransitionDto;
  timeline: LoanTransitionEventDto[];
  noticeQuote: PayoffQuoteDto | null;
  /** PR-3 — the linked deal's LIVE pipeline stage ("Closed" once boarded);
   *  the derived-state fallback when the closing-time hook missed. */
  linkedDealStage?: string | null;
  linkedDealNumber?: string | null;
}

/** PR-3 — an in-progress deal offered by the AwaitingLinkedDeal picker. */
export interface LinkableDealDto {
  id: string;
  dealNumber: string;
  businessName: string | null;
  stage: string;
  createdAt: string;
}

export interface PayoffQuoteDto {
  id: string | null;
  loanId: string;
  asOfDate: string;
  outstandingPrincipal: number;
  accruedInterest: number;
  openArrears: number;
  prepaymentAdminFee: number;
  prepaymentAdminFeePct: number;
  yieldMaintenancePremium: number;
  totalPayoff: number;
  isBindingStatement: boolean;
  createdAt: string | null;
}

export interface TransitionConsoleRowDto {
  id: string;
  loanId: string;
  loanNumber: string;
  type: LoanTransitionType;
  status: LoanTransitionStatus;
  tenderDate: string | null;
  daysToTender: number | null;
  initiatedAt: string | null;
  createdAt: string;
}

export interface CreateLoanTransitionRequest {
  type: LoanTransitionType;
  initiate?: boolean;
  parametersJson?: string | null;
  note?: string | null;
}

export interface ChangeLoanTransitionStatusRequest {
  targetStatus: LoanTransitionStatus;
  reason?: string | null;
  overrideIrrevocableNotice?: boolean;
  linkedDealId?: string | null;
  scheduledClosingDate?: string | null;
}

export interface RecordPrepaymentNoticeRequest {
  /** ISO date (yyyy-MM-dd) — the committed tender date. */
  tenderDate: string;
  noticeDeliveredAt?: string | null;
  note?: string | null;
}

/** PR-4a — execute a Scheduled assumption/hardship transition (D-26).
 *  Exactly one of the person/entity ids names the new obligor; the portal
 *  account is resolved server-side (attachment, then email) when not given. */
export interface ExecuteAssumptionRequest {
  newBorrowerPersonId?: string | null;
  newBorrowerEntityId?: string | null;
  newBorrowerPortalUserId?: string | null;
  note?: string | null;
}

/** PR-4b — execute a Scheduled BMSP maturity extension: the amendment-grade
 *  maturity/term advance (1–5 years, at most two extensions per the BLA) plus
 *  the 1% extension fee collected through WF1 P14. The rate never changes. */
export interface ExecuteBmspExtensionRequest {
  extensionYears: number;
  note?: string | null;
}

/** PR-5 — the path-wizard "Ask AI" coach (the RecordPaymentExplain shape).
 *  Context is a snapshot only — the grounding numbers are loaded server-side. */
export interface TransitionExplainTurn {
  role: "user" | "assistant";
  content: string;
}

export interface TransitionExplainContext {
  pathType?: LoanTransitionType | null;
  pathFacts?: string | null;
  stepKey?: string | null;
  transitionId?: string | null;
}

export interface TransitionExplainRequest {
  question: string | null;
  history: TransitionExplainTurn[] | null;
  context?: TransitionExplainContext | null;
}

export interface TransitionExplainDto {
  answer: string;
}

/** Transitions PR-2 — the server-derived BMSP covenant countdown for a
 *  balloon loan. Days figures are signed (negative = passed). Null from the
 *  per-loan endpoint = not a live balloon loan. */
export interface BmspCountdownDto {
  loanId: string;
  anchor: string;
  termMonths: number;
  currentMonthNumber: number;
  hasBmspWindow: boolean;
  windowOpen: string | null;
  applicationDeadline: string | null;
  balloonMaturity: string;
  daysToWindowOpen: number | null;
  daysToDeadline: number | null;
  daysToMaturity: number;
  windowMissed: boolean;
  hasOpenBmspTransition: boolean;
}

export interface BmspQueueRowDto {
  loanId: string;
  loanNumber: string;
  countdown: BmspCountdownDto;
}

export const loanTransitionsApi = {
  list: (loanId: string) =>
    api.get<LoanTransitionDto[]>(`/portfolio-admin/loans/${loanId}/transitions`),
  get: (loanId: string, transitionId: string) =>
    api.get<LoanTransitionDetailDto>(
      `/portfolio-admin/loans/${loanId}/transitions/${transitionId}`,
    ),
  create: (loanId: string, body: CreateLoanTransitionRequest) =>
    api.post<LoanTransitionDto>(`/portfolio-admin/loans/${loanId}/transitions`, body),
  changeStatus: (loanId: string, transitionId: string, body: ChangeLoanTransitionStatusRequest) =>
    api.post<LoanTransitionDto>(
      `/portfolio-admin/loans/${loanId}/transitions/${transitionId}/status`,
      body,
    ),
  recordPrepaymentNotice: (
    loanId: string,
    transitionId: string,
    body: RecordPrepaymentNoticeRequest,
  ) =>
    api.post<LoanTransitionDto>(
      `/portfolio-admin/loans/${loanId}/transitions/${transitionId}/prepayment-notice`,
      body,
    ),
  quotePreview: (loanId: string, asOf?: string) =>
    api.get<PayoffQuoteDto>(
      `/portfolio-admin/loans/${loanId}/payoff-quote`,
      asOf ? { asOf } : undefined,
    ),
  quoteIssue: (loanId: string, asOf?: string) =>
    api.post<PayoffQuoteDto>(
      `/portfolio-admin/loans/${loanId}/payoff-quote${asOf ? `?asOf=${asOf}` : ""}`,
    ),
  quoteHistory: (loanId: string) =>
    api.get<PayoffQuoteDto[]>(`/portfolio-admin/loans/${loanId}/payoff-quote/history`),
  consoleOpen: () =>
    api.get<TransitionConsoleRowDto[]>(`/portfolio-admin/transitions`),
  bmspCountdown: (loanId: string) =>
    api.get<BmspCountdownDto | null>(
      `/portfolio-admin/loans/${loanId}/transitions/bmsp-countdown`,
    ),
  bmspQueue: () =>
    api.get<BmspQueueRowDto[]>(`/portfolio-admin/transitions/bmsp-countdown`),
  linkableDeals: (q?: string) =>
    api.get<LinkableDealDto[]>(
      `/portfolio-admin/transitions/linkable-deals`,
      q ? { q } : undefined,
    ),
  executeAssumption: (loanId: string, transitionId: string, body: ExecuteAssumptionRequest) =>
    api.post<LoanTransitionDto>(
      `/portfolio-admin/loans/${loanId}/transitions/${transitionId}/execute-assumption`,
      body,
    ),
  executeBmspExtension: (
    loanId: string,
    transitionId: string,
    body: ExecuteBmspExtensionRequest,
  ) =>
    api.post<LoanTransitionDto>(
      `/portfolio-admin/loans/${loanId}/transitions/${transitionId}/execute-bmsp-extension`,
      body,
    ),
  explainPath: (loanId: string, body: TransitionExplainRequest) =>
    api.post<TransitionExplainDto>(
      `/portfolio-admin/loans/${loanId}/transitions/explain`,
      body,
    ),
};

/** Layperson description per exit path — shown in the type picker and on
 *  the transition card. */
export const TRANSITION_TYPE_INFO: Record<
  LoanTransitionType,
  { label: string; description: string }
> = {
  PhoenixBackedResale: {
    label: "Phoenix-Backed Resale",
    description:
      "A new buyer assumes the existing loan and the seller's equity gain becomes a new note. No yield maintenance is charged.",
  },
  SaleWithAssumption: {
    label: "Sale with Loan Assumption",
    description:
      "The business sells and the new buyer takes over the existing loan unchanged. Assumption fees apply; the exiting buyer's guaranty continues until the tiered release conditions are met.",
  },
  BankRefinance: {
    label: "Bank / SBA Refinance",
    description:
      "The buyer refinances elsewhere and pays the loan off in full at the Yield Maintenance Amount.",
  },
  CashPayoff: {
    label: "Full Cash Payoff",
    description:
      "The buyer pays the loan off in full at the Yield Maintenance Amount. A quote is non-binding; a delivered Prepayment Notice is irrevocable.",
  },
  HardshipExit: {
    label: "Hardship Exit",
    description:
      "An expedited assumption on documented owner incapacity (terminal diagnosis, permanent disability, or death). Standard assumption fees and guaranties apply.",
  },
  BmspFullPayoff: {
    label: "BMSP — Full Payoff",
    description: "The balloon is paid in full at maturity under the Balloon Maturity Support Program.",
  },
  BmspThirdPartyRefinance: {
    label: "BMSP — Third-Party Refinancing",
    description: "The balloon is refinanced with an outside lender at maturity under the BMSP.",
  },
  BmspMaturityExtension: {
    label: "BMSP — Maturity Extension",
    description:
      "A note-holder-approved extension of 1–5 years. The rate stays at the original 10.00% (no reprice); the extension fee is 1.00% of outstanding principal.",
  },
  RightSizeRefinance: {
    label: "Right-Size Refinance",
    description:
      "The business stabilized below the loan balance: the existing loan is paid off in full (shortfall + yield maintenance + fees) and a new smaller loan is originated to a new buyer at fair value.",
  },
};

export const TRANSITION_STATUS_LABEL: Record<LoanTransitionStatus, string> = {
  Draft: "Draft",
  Initiated: "Initiated",
  AwaitingLinkedDeal: "Awaiting linked deal",
  PendingApproval: "Pending approval",
  Scheduled: "Scheduled",
  Completed: "Completed",
  Canceled: "Canceled",
  Lapsed: "Lapsed (missed tender)",
};

/** The four payoff-family types (quote → irrevocable notice → tender). */
export const PAYOFF_FAMILY: readonly LoanTransitionType[] = [
  "CashPayoff",
  "BankRefinance",
  "BmspFullPayoff",
  "BmspThirdPartyRefinance",
] as const;

export const isPayoffFamily = (type: LoanTransitionType): boolean =>
  (PAYOFF_FAMILY as readonly string[]).includes(type);
